/**
 * Client API for BOM Adapter and Material Requirement Batches.
 * Conforms to Sections C5, E1-E2, G3 of docs/superpowers/plans/2026-10-02-bom-explosion-canonical-spec.md
 */
import { apiFetch } from '../apiFetch'
import {
  ProcurementApiClientError,
  type ProcurementApiErrorResponse,
} from './apiClient'

export type CanonicalBomSource = 'wood' | 'hardware' | 'foam'
export type StandardAssemblyType = 'assembled' | 'kd'

export interface ExplosionInputLine {
  product_id: string
  product_code: string
  order_qty: number
  attributes: {
    color?: string
    wood_type?: string
    assembly_type: StandardAssemblyType
    packaging_type?: 'thuong' | 'ecom'
  }
  bom_sources: CanonicalBomSource[]
  override_wood_config_id?: string
  override_foam_config_id?: string
}

export interface ExplosionContribution {
  product_id: string
  product_code: string
  qty: number
  source_bom_line_id: string
  bom_source: CanonicalBomSource
  /** Ghi chú của dòng BOM; không có thì bỏ trống. */
  note?: string | null
  /** Thuộc tính của cấu hình BOM đã chọn (cột Kết cấu / Đóng gói). */
  assembly_type?: 'assembled' | 'kd'
  packaging_option?: 'thuong' | 'ecom' | 'all'
}

export interface ExplosionResultLine {
  material_id: string | null
  material_code: string | null
  material_name: string
  material_name_raw?: string
  material_color?: string | null
  uom: string
  qty_gross: number
  contributions: ExplosionContribution[]
  resolution_status: 'resolved' | 'unresolved'
  resolution_notes?: string
  material_spec?: Record<string, unknown> | null
  material_spec_raw?: string | null
}

export interface ExplosionRunResult {
  lines: ExplosionResultLine[]
  total_materials: number
  resolved_materials: number
  unresolved_materials: number
}

export interface CreateBatchContributionInput {
  product_id: string
  product_code: string
  qty: number
  source_bom_line_id: string
  bom_source: CanonicalBomSource
}

export interface CreateBatchLineInput {
  material_id?: string | null
  material_code?: string | null
  material_name: string
  uom: string
  qty_gross: number
  qty_actual_buy?: number | null
  resolution_status: 'resolved' | 'unresolved'
  notes?: string | null
  material_spec?: Record<string, unknown> | null
  material_spec_raw?: string | null
  contributions: CreateBatchContributionInput[]
}

export interface CreateMaterialRequirementBatchInput {
  title: string
  bom_source: string
  supplier_id?: string | null
  input_snapshot: ExplosionInputLine[]
  lines: CreateBatchLineInput[]
}

export interface MaterialRequirementContributionRow {
  id: string
  requirement_line_id: string
  product_id: string
  product_code: string
  product_name?: string
  qty: number
  source_bom_line_id: string
  bom_source: string
  created_at: string
}

export interface MaterialRequirementLineRow {
  id: string
  batch_id: string
  material_id: string | null
  material_code: string | null
  material_name: string
  uom: string
  qty_gross: number
  qty_actual_buy: number
  resolution_status: string
  notes: string | null
  material_spec?: Record<string, unknown> | null
  material_spec_raw?: string | null
  created_at: string
  contributions?: MaterialRequirementContributionRow[]
}

export interface MaterialRequirementBatchRow {
  id: string
  title: string
  bom_source: string
  supplier_id: string | null
  input_snapshot: ExplosionInputLine[]
  status: string
  created_by: string
  created_at: string
  updated_at: string
  lines?: MaterialRequirementLineRow[]
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfError(res: Response, body: unknown, fallbackMessage: string): void {
  if (res.ok) return
  const err = body as ProcurementApiErrorResponse
  const code = err?.error?.code ?? 'HTTP_ERROR'
  const message = err?.error?.message ?? `${fallbackMessage} (${res.status})`
  const status = err?.error?.status ?? res.status
  throw new ProcurementApiClientError(code, message, status, err?.error?.details)
}

export async function resolveBomAdapterApi(
  items: ExplosionInputLine[],
): Promise<ExplosionRunResult> {
  const res = await apiFetch('/api/bom-adapter/resolve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  })
  const body = await parseJson<{ ok: boolean; data: ExplosionRunResult } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Lỗi khi tính toán BOM adapter')
  return (body as { data: ExplosionRunResult }).data
}

export async function createMaterialRequirementBatchApi(
  payload: CreateMaterialRequirementBatchInput,
): Promise<MaterialRequirementBatchRow> {
  const res = await apiFetch('/api/material-requirements', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await parseJson<{ ok: boolean; data: MaterialRequirementBatchRow } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Lỗi khi tạo đợt nhu cầu vật tư')
  return (body as { data: MaterialRequirementBatchRow }).data
}

export async function getMaterialRequirementBatchApi(
  id: string,
): Promise<MaterialRequirementBatchRow> {
  const res = await apiFetch(`/api/material-requirements/${encodeURIComponent(id)}`, {
    method: 'GET',
  })
  const body = await parseJson<{ ok: boolean; data: MaterialRequirementBatchRow } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Lỗi khi lấy thông tin đợt nhu cầu vật tư')
  return (body as { data: MaterialRequirementBatchRow }).data
}

export interface PoLineTraceabilityBreakdownItem {
  product_id: string
  product_code: string
  product_name?: string
  product_order_qty?: number
  bom_source: string
  contribution_qty: number
}

export interface PoLineTraceabilityResult {
  po_line_id: string
  material_name: string
  material_code: string | null
  order_qty: number
  order_uom: string
  qty_gross_bom: number
  variance_qty: number
  variance_note: string | null
  batch_id: string
  batch_title: string
  batch_created_at: string
  breakdown: PoLineTraceabilityBreakdownItem[]
}

export async function getPoLineTraceabilityApi(
  poLineId: string,
): Promise<PoLineTraceabilityResult> {
  const res = await apiFetch(`/api/purchase-orders/lines/${encodeURIComponent(poLineId)}/traceability`, {
    method: 'GET',
  })
  const body = await parseJson<{ ok: boolean; data: PoLineTraceabilityResult } | ProcurementApiErrorResponse>(res)
  throwIfError(res, body, 'Lỗi khi lấy thông tin truy vết PO line')
  return (body as { data: PoLineTraceabilityResult }).data
}
